/**
 * SharedRunningComparison — public view of a shared running comparison (read-only).
 * Thin wrapper: fetches shared data and delegates all tab UI to RunningComparisonView.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../theme';
import { getSharedRunningComparison, unshareRunningComparison } from '../../api/runningShareApi';

import RunningComparisonView from '../../components/running/RunningComparisonView';
import ComparisonHeader from '../../components/running/comparison/ComparisonHeader';

export default function SharedRunningComparison() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { shareId } = useParams();

  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [unshareDialogOpen, setUnshareDialogOpen] = useState(false);
  const [unshareLoading, setUnshareLoading] = useState(false);

  useEffect(() => {
    if (!shareId) return;
    const fetchSharedComparison = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getSharedRunningComparison(shareId);
        setShareData(data);
      } catch (err) {
        console.error('Failed to load shared comparison:', err);
        setError(err.response?.data?.detail || 'Failed to load shared comparison');
      } finally {
        setLoading(false);
      }
    };
    fetchSharedComparison();
  }, [shareId]);

  const handleUnshare = async () => {
    try {
      setUnshareLoading(true);
      await unshareRunningComparison(shareId);
      setUnshareDialogOpen(false);
      navigate('/running');
    } catch (err) {
      console.error('Failed to unshare:', err);
      alert('Failed to unshare comparison');
    } finally {
      setUnshareLoading(false);
    }
  };

  if (loading) {
    return (
      <Box m={isMobile ? '10px' : '20px'} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !shareData) {
    return (
      <Box m={isMobile ? '10px' : '20px'}>
        <Alert severity="error">{error || 'Shared comparison not found'}</Alert>
        <Box mt={2}>
          <Button variant="outlined" onClick={() => navigate('/running')} sx={{ color: colors.grey[100] }}>
            Back to Running Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  const analysisA = shareData.workout_a_data;
  const analysisB = shareData.workout_b_data;
  const alignment = shareData.alignment_result;
  const checkpoints = shareData.checkpoint_comparison;
  const resampled = alignment?.resampled || [];

  const headerSection = (
    <>
      {/* Title row */}
      <Box
        mb={3}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ShareIcon sx={{ color: colors.blueAccent[500] }} />
            <Typography variant={isMobile ? 'h4' : 'h2'} fontWeight="bold" color={colors.grey[100]}>
              {shareData.display_name || 'Shared Running Comparison'}
            </Typography>
          </Box>
          {shareData.description && (
            <Typography variant="body1" color={colors.grey[300]} mb={1}>
              {shareData.description}
            </Typography>
          )}
          <Typography variant="body2" color={colors.grey[400]}>
            {shareData.is_anonymous ? 'Anonymous share' : `Shared by ${shareData.owner_name || 'Unknown'}`}
            {' • '}
            {new Date(shareData.shared_at).toLocaleDateString()}
            {shareData.view_count > 0 && ` • ${shareData.view_count} views`}
          </Typography>
        </Box>
        <Box display="flex" gap={isMobile ? 1 : 2}>
          {shareData.is_owner && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setUnshareDialogOpen(true)}
              sx={{
                minWidth: isMobile ? 'auto' : undefined,
                px: isMobile ? 2 : undefined,
                fontSize: isMobile ? '0.75rem' : undefined,
              }}
            >
              Unshare
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate('/running')}
            sx={{
              color: colors.grey[100],
              borderColor: colors.primary[400],
              minWidth: isMobile ? 'auto' : undefined,
              px: isMobile ? 2 : undefined,
              fontSize: isMobile ? '0.75rem' : undefined,
              '&:hover': { borderColor: colors.primary[300], backgroundColor: colors.primary[700] },
            }}
          >
            {isMobile ? 'Dashboard' : 'Back to Running Dashboard'}
          </Button>
        </Box>
      </Box>

      {/* DTW course match warning */}
      {alignment?.course_match === 'different' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These workouts appear to follow different courses (DTW cost: {alignment.dtw_cost_m?.toFixed(0)}m). Comparison may be less meaningful.
        </Alert>
      )}

      <ComparisonHeader
        analysisA={analysisA}
        analysisB={analysisB}
        alignment={alignment}
        labelA={shareData.label_a}
        labelB={shareData.label_b}
        descriptionA={shareData.a_description}
        descriptionB={shareData.b_description}
        sourceA={shareData.a_source}
        sourceB={shareData.b_source}
        deviceNameA={shareData.a_device_name}
        deviceNameB={shareData.b_device_name}
      />
    </>
  );

  return (
    <>
      <RunningComparisonView
        analysisA={analysisA}
        analysisB={analysisB}
        alignment={alignment}
        resampled={resampled}
        checkpoints={checkpoints}
        headerSection={headerSection}
        extraDialogs={
          <Dialog open={unshareDialogOpen} onClose={() => setUnshareDialogOpen(false)}>
            <DialogTitle>Unshare Comparison?</DialogTitle>
            <DialogContent>
              <Typography>
                This will delete the shareable link. The original analyses will remain in your account.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUnshareDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleUnshare}
                color="error"
                disabled={unshareLoading}
                startIcon={unshareLoading && <CircularProgress size={16} />}
              >
                {unshareLoading ? 'Unsharing...' : 'Unshare'}
              </Button>
            </DialogActions>
          </Dialog>
        }
      />
    </>
  );
}
